<script setup lang="ts">
import { nextTick, ref } from 'vue';
import CloseIcon from '@assets/icons/close.svg';
import EarIcon from '@assets/icons/ear.svg';
import PlayIcon from '@assets/icons/play.svg';
import { useI18n } from 'vue-i18n';
import Button from '@components/Button.vue';

const props = defineProps<{
    src: string;
    poster: string;
    mimeType: string;
    signLanguage?: string;
    captions?: string;
}>();

const video = ref<HTMLVideoElement | null>(null);
const src = ref(props.src);
const isPlaying = ref(false);
const isAlternateSource = ref(false);

const { t } = useI18n();

const togglePlay = async () => {
    if (!video.value) return;

    if (isPlaying.value) {
        video.value.pause();
    } else {
        await video.value.play();
    }
};

const toggleSource = async () => {
    if (!props.signLanguage || !video.value) return;

    if (isPlaying.value) {
        video.value.pause();
    }

    if (isAlternateSource.value) {
        src.value = props.src;
    } else {
        src.value = props.signLanguage;
    }

    isAlternateSource.value = !isAlternateSource.value;

    await nextTick();
    video.value.load();
    video.value.currentTime = 0;
    video.value.play();
};
</script>

<template>
    <div class="shadow-card relative overflow-hidden rounded">
        <Button
            v-if="signLanguage"
            class="bg-background absolute top-10 right-10 z-20"
            @click="toggleSource"
        >
            <template v-if="isAlternateSource">
                <CloseIcon />
                <span>{{ t('Watch Original Version') }}</span>
            </template>
            <template v-else>
                <EarIcon />
                <span>{{ t('Watch in Sign Language') }}</span>
            </template>
        </Button>

        <button
            class="group absolute inset-0 z-10 flex cursor-pointer items-center justify-center"
            @click="togglePlay"
            aria-label="Play Video"
            :class="{
                'pointer-events-none': isPlaying,
            }"
        >
            <span
                class="hover:bg-primary hover:text-primary-foreground hover:border-primary focusable flex size-30 items-center justify-center rounded-full border-2 border-white text-white transition-opacity active:border-black active:bg-black active:text-white"
                :class="{
                    'opacity-0': isPlaying,
                }"
            >
                <PlayIcon class="ml-4" />
            </span>
        </button>

        <video
            ref="video"
            :poster="poster"
            :controls="isPlaying"
            playsinline
            @play="isPlaying = true"
            @pause="isPlaying = false"
            @ended="isPlaying = false"
            class="grayscale:grayscale"
        >
            <source
                :type="mimeType"
                :src="src"
            />
            <track
                v-if="captions"
                label="Untertitel"
                kind="subtitles"
                :src="captions"
                default
            />
        </video>
    </div>
</template>
